import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './UserProfile.css';

interface UserProfileProps {
  user: { id: string; name: string; telegramPhotoUrl?: string | null };
  license: any;
  isScanning?: boolean;
}

const RANK_COLOR: Record<string, string> = {
  injected: '#E8132E',
  breached:  '#f59e0b',
  ghosted:   '#a855f7',
};

function xpBar(current: number, next: number) {
  if (next <= 0) return { pct: 100, bar: '██████████████' };
  const p = Math.min(100, (current / next) * 100);
  const filled = Math.round((p / 100) * 14);
  return { pct: p, bar: '█'.repeat(filled) + '░'.repeat(14 - filled) };
}

function fmtN(n: number | undefined | null) {
  return (n ?? 0).toLocaleString('en-US');
}

const EyeIcon = ({ open }: { open: boolean }) => open ? (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
) : (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const SaveIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/>
    <polyline points="7 3 7 8 15 8"/>
  </svg>
);

const UserProfile: React.FC<UserProfileProps> = ({ user, license, isScanning }) => {
  const rank      = (license?.rank || 'Ghosted') as string;
  const rankKey   = rank.toLowerCase();
  const rankColor = RANK_COLOR[rankKey] || '#a855f7';
  const level     = license?.currentLevel ?? 1;
  const curXp     = license?.currentLevelXp ?? 0;
  const nextXp    = license?.nextLevelXp ?? 250;
  const totalXp   = license?.totalXp ?? 0;
  const totalScan = license?.totalScan ?? 0;
  const { pct, bar } = xpBar(curXp, nextXp);

  const vpsCount = (() => {
    const list = license?.vpsList || [];
    if (list.length > 0) return list.length;
    let c = 0;
    if (license?.vpsIp)  c++;
    if (license?.vpsIp2) c++;
    return c;
  })();

  const displayName = user.name || license?.username || 'Operator';
  const tgId        = user.id;

  // ── Telegram config state ────────────────────────────────
  const [tg, setTg] = useState({ botToken: '', channelValid: '', channelInvalid: '', channelStats: '' });
  const [showToken, setShowToken] = useState(false);
  const [tgLoading, setTgLoading] = useState(false);
  const [tgSaving, setTgSaving] = useState(false);
  const [tgMsg, setTgMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [smtpEmail, setSmtpEmail] = useState('');

  useEffect(() => {
    setTgLoading(true);
    Promise.all([
      apiService.getTelegramConfig().catch(() => null),
      apiService.getSmtpEmail().catch(() => null),
    ]).then(([cfg, emailData]) => {
      if (cfg)       setTg(cfg);
      if (emailData) setSmtpEmail(emailData.smtpTestEmailAddress || '');
    }).finally(() => setTgLoading(false));
  }, []);

  useEffect(() => {
    if (!tgMsg) return;
    const t = setTimeout(() => setTgMsg(null), 3000);
    return () => clearTimeout(t);
  }, [tgMsg]);


  const saveAll = async () => {
    if (isScanning) { setTgMsg({ type: 'err', text: 'Locked during scan' }); return; }
    if (!tg.botToken) {
      setTgMsg({ type: 'err', text: 'Bot token required' }); return;
    }
    setTgSaving(true);
    try {
      // SMTP save is best-effort — don't let a missing route block Telegram
      await Promise.all([
        apiService.updateTelegramConfig(tg),
        apiService.updateSmtpEmail(smtpEmail).catch(() => {}),
      ]);
      setTgMsg({ type: 'ok', text: 'Saved' });
    } catch {
      setTgMsg({ type: 'err', text: 'Save failed' });
    } finally {
      setTgSaving(false);
    }
  };

  return (
    <div className="uprof-page">
      <div className="uprof-card" style={{ '--rank-color': rankColor } as React.CSSProperties}>

        {/* Top glow + scanlines (CSS ::before / ::after) */}
        <div className="uprof-corner uprof-corner--tl" />
        <div className="uprof-corner uprof-corner--br" />

        {/* ── SECTION 1 : Identity ─────────────────────── */}
        <div className="uprof-identity-row">
          {/* Avatar */}
          <div className="uprof-avatar-wrap">
            <div className="uprof-avatar-ring" />
            {user.telegramPhotoUrl ? (
              <img
                src={user.telegramPhotoUrl}
                alt=""
                className="uprof-avatar-img"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                  const fb = e.currentTarget.nextElementSibling as HTMLElement | null;
                  if (fb) fb.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="uprof-avatar-fallback"
              style={user.telegramPhotoUrl ? { display: 'none' } : undefined}
            >
              {displayName.slice(0, 2).toUpperCase()}
            </div>
          </div>

          {/* Name / ID / rank / XP */}
          <div className="uprof-identity-info">
            <div className="uprof-identity-top">
              <div>
                <h2 className="uprof-username">@{displayName}</h2>
                <p className="uprof-tgid">TG #{tgId}</p>
              </div>
              <span
                className="uprof-rank-badge"
                style={{ background: `${rankColor}22`, color: rankColor, borderColor: `${rankColor}55` }}
              >
                {rank.toUpperCase()}
              </span>
            </div>

            <div className="uprof-xp-block">
              <div className="uprof-xp-row">
                <span className="uprof-xp-label">LVL {level}</span>
                <span className="uprof-xp-pct">{pct.toFixed(1)}%</span>
              </div>
              <div className="uprof-xp-track">
                <div className="uprof-xp-fill" style={{ width: `${pct}%`, background: rankColor }} />
              </div>
              <p className="uprof-xp-nums">{fmtN(curXp)} / {fmtN(nextXp)} XP</p>
            </div>
          </div>
        </div>

        {/* Mono bar */}
        <div className="uprof-mono-bar"><code>{bar}</code></div>

        {/* ── SECTION 2 : Stats strip ──────────────────── */}
        <div className="uprof-stats">
          <div className="uprof-stat">
            <span className="uprof-stat-label">Level</span>
            <span className="uprof-stat-value">{level}</span>
          </div>
          <div className="uprof-stat">
            <span className="uprof-stat-label">Total XP</span>
            <span className="uprof-stat-value">{fmtN(totalXp)}</span>
          </div>
          <div className="uprof-stat">
            <span className="uprof-stat-label">Scans</span>
            <span className="uprof-stat-value">{fmtN(totalScan)}</span>
          </div>
        </div>

        {/* ── SECTION 3 : Server indicator ─────────────── */}
        <div className="uprof-server-row">
          <span className="uprof-server-dot" style={{ background: vpsCount > 0 ? '#22c55e' : '#52525b' }} />
          <span className="uprof-server-label">
            {vpsCount > 0
              ? `${vpsCount} server${vpsCount > 1 ? 's' : ''} online`
              : 'No server configured'}
          </span>
          {license?.vpsIp && (
            <span className="uprof-server-ip">{license.vpsIp}:{license.vpsPort}</span>
          )}
        </div>

        {/* ── DIVIDER ───────────────────────────────────── */}
        <div className="uprof-divider">
          <span className="uprof-divider-label">Telegram</span>
        </div>

        {/* ── SECTION 4 : Telegram config ──────────────── */}
        {tgLoading ? (
          <div className="uprof-tg-loading">Loading config…</div>
        ) : (
          <div className="uprof-tg-grid">
            {/* Bot Token */}
            <div className="uprof-tg-field uprof-tg-field--full">
              <label className="uprof-tg-label">Bot Token</label>
              <div className="uprof-tg-input-wrap">
                <input
                  type={showToken ? 'text' : 'password'}
                  className="uprof-tg-input"
                  placeholder="1234567890:ABCdef..."
                  value={tg.botToken}
                  onChange={e => setTg(p => ({ ...p, botToken: e.target.value }))}
                  disabled={isScanning}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="uprof-tg-eye"
                  onClick={() => setShowToken(v => !v)}
                  aria-label="Toggle token visibility"
                >
                  <EyeIcon open={showToken} />
                </button>
              </div>
            </div>

            {/* Channel IDs */}
            <div className="uprof-tg-field">
              <label className="uprof-tg-label">Valid hits chat</label>
              <input
                type="text"
                className="uprof-tg-input"
                placeholder="-100123456789"
                value={tg.channelValid}
                onChange={e => setTg(p => ({ ...p, channelValid: e.target.value }))}
                disabled={isScanning}
              />
            </div>

            <div className="uprof-tg-field">
              <label className="uprof-tg-label">Invalid hits chat</label>
              <input
                type="text"
                className="uprof-tg-input"
                placeholder="-100123456789"
                value={tg.channelInvalid}
                onChange={e => setTg(p => ({ ...p, channelInvalid: e.target.value }))}
                disabled={isScanning}
              />
            </div>

            <div className="uprof-tg-field">
              <label className="uprof-tg-label">Stats chat</label>
              <input
                type="text"
                className="uprof-tg-input"
                placeholder="-100123456789"
                value={tg.channelStats}
                onChange={e => setTg(p => ({ ...p, channelStats: e.target.value }))}
                disabled={isScanning}
              />
            </div>

            {/* SMTP Test Email */}
            <div className="uprof-tg-field uprof-tg-field--full">
              <label className="uprof-tg-label">SMTP Test Email</label>
              <input
                type="email"
                className="uprof-tg-input"
                placeholder="test@example.com"
                value={smtpEmail}
                onChange={e => setSmtpEmail(e.target.value)}
                disabled={isScanning}
                autoComplete="off"
              />
            </div>

            {/* Single Save row */}
            <div className="uprof-tg-save-row">
              {tgMsg && (
                <span className={`uprof-tg-msg${tgMsg.type === 'err' ? ' uprof-tg-msg--err' : ''}`}>
                  {tgMsg.text}
                </span>
              )}
              <button
                type="button"
                className="uprof-tg-save"
                onClick={saveAll}
                disabled={tgSaving || isScanning}
              >
                <SaveIcon />
                {tgSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default UserProfile;
